import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RichTextEditor } from '../ui/rich-text-editor';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { Shield, Plus, Save, Trash2, Edit2, X, RefreshCw } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

type PrivacyPolicy = {
  id?: string;
  _id?: string;
  title: string;
  content: string;
};

interface PrivacyPolicyItemProps {
  item: PrivacyPolicy;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (id: string, data: PrivacyPolicy) => void;
  onDelete: (item: PrivacyPolicy) => void;
  effectiveId: (t: PrivacyPolicy) => string | undefined;
}

const PrivacyPolicyItem = ({
  item,
  index,
  isEditing,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
  effectiveId
}: PrivacyPolicyItemProps) => {
  const [state, setState] = useState<PrivacyPolicy>(item);

  useEffect(() => {
    setState(item);
  }, [item]);

  const id = effectiveId(item);

  return (
    <div className="rounded p-3 flex items-start gap-3 relative group border border-gray-100 bg-white hover:border-gray-200 transition-colors">
      <div className="absolute -left-2 top-3 -ml-4 w-6 text-right text-gray-400 font-mono text-sm select-none">
        {index + 1}.
      </div>
      <div className="flex-1 space-y-2">
        {isEditing ? (
          <div className="space-y-3">
            <Input
              placeholder="Section Title"
              value={state.title}
              onChange={(e) => setState({ ...state, title: e.target.value })}
            />
            <RichTextEditor
              value={state.content}
              onChange={(val) => setState({ ...state, content: val })}
              placeholder="Section Content"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" onClick={() => id && onSave(id, state)}>
                <Save className="h-4 w-4 mr-1" /> Save
              </Button>
              <Button variant="outline" size="sm" onClick={onCancelEdit}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="font-medium text-lg">{state.title}</div>
            <div
              className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: state.content }}
            />
          </div>
        )}

        {!isEditing && (
          <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit2 className="h-4 w-4 mr-1" /> Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(item)}
              className="text-red-600 hover:text-red-700 border-red-200"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const PrivacyPolicyManagement = () => {
  const [items, setItems] = useState<PrivacyPolicy[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState<PrivacyPolicy>({ title: '', content: '' });
  const [editingId, setEditingId] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    policyId: string | null;
    policyTitle: string;
  }>({
    open: false,
    policyId: null,
    policyTitle: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const effectiveId = (t: PrivacyPolicy) => t.id || (typeof t._id === 'string' ? t._id : (t._id as any)?.toString?.());

  const [bgImage, setBgImage] = useState<string>('');
  const [uploadingBg, setUploadingBg] = useState(false);

  // Fetch items + settings
  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getPrivacyPolicies();
      setItems(Array.isArray(data) ? data : (data?.items || []));

      try {
        const settings = await apiClient.getSectionSettings('privacy_policy_settings');
        if (settings?.config?.backgroundImage) {
          setBgImage(settings.config.backgroundImage);
        } else if (settings?.backgroundImage) {
          setBgImage(settings.backgroundImage);
        }
      } catch (err) {
        console.warn('Failed to load page settings', err);
      }

    } catch (e: any) {
      console.error('Failed to load privacy policies', e);
    } finally {
      setLoading(false);
    }
  };

  const handleBgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingBg(true);
    try {
      // 1. Upload image
      const res = await apiClient.uploadSectionImage('privacy_policy_settings', file);
      const url = res?.url || res?.secure_url;

      if (url) {
        // 2. Save setting
        await apiClient.updateSectionSettings('privacy_policy_settings', { config: { backgroundImage: url } });
        setBgImage(url);
        toast.success('Background image updated');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload background');
    } finally {
      setUploadingBg(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleCreate = async () => {
    if (!creating.title || !creating.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const res = await apiClient.createPrivacyPolicy(creating);
      const created = res?.data || res;
      setItems([...items, created]);
      setCreating({ title: '', content: '' });
      toast.success('Privacy policy section added');
      await fetchItems();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add section');
    }
  };

  const handleUpdate = async (id: string, updated: PrivacyPolicy) => {
    try {
      const saved = await apiClient.updatePrivacyPolicy(id, {
        title: updated.title,
        content: updated.content
      });
      setItems(items.map(i => (effectiveId(i) === id ? saved : i)));
      setEditingId(null);
      toast.success('Section updated');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update section');
    }
  };

  const openDeleteDialog = (item: PrivacyPolicy) => {
    const id = effectiveId(item);
    if (!id) return;
    setDeleteDialog({
      open: true,
      policyId: id,
      policyTitle: item.title
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.policyId) return;
    setIsDeleting(true);
    try {
      await apiClient.deletePrivacyPolicy(deleteDialog.policyId);
      setItems(items.filter(i => effectiveId(i) !== deleteDialog.policyId));
      setDeleteDialog(prev => ({ ...prev, open: false }));
      toast.success('Section deleted');
    } catch (e: any) {
      if (e?.status === 404 || e?.message?.includes('Not found')) {
        setItems(items.filter(i => effectiveId(i) !== deleteDialog.policyId));
        setDeleteDialog(prev => ({ ...prev, open: false }));
        toast.success('Section deleted (was not found on server)');
      } else {
        toast.error(e?.message || 'Failed to delete section');
        setDeleteDialog(prev => ({ ...prev, open: false }));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Page Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Background Image (Public Page)</label>
              <div className="flex items-start gap-4">
                {bgImage && (
                  <div className="relative w-32 h-20 border rounded overflow-hidden bg-gray-100">
                    <img src={bgImage} alt="Background" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleBgUpload}
                    disabled={uploadingBg}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 1920x1080px or higher. This will be shown with reduced opacity on the public privacy policy page.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Privacy Policy Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 p-4 border rounded-lg bg-gray-50/50">
            <h3 className="font-medium">Add New Section</h3>
            <Input
              placeholder="Section Title (e.g., 1. Introduction)"
              value={creating.title}
              onChange={(e) => setCreating({ ...creating, title: e.target.value })}
            />
            <RichTextEditor
              value={creating.content}
              onChange={(val) => setCreating({ ...creating, content: val })}
              placeholder="Section Content"
            />
            <Button onClick={handleCreate} disabled={loading || !creating.title || !creating.content}>
              <Plus className="h-4 w-4 mr-2" /> Add Section
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-lg">Existing Sections ({items.length})</h3>
            <Button variant="outline" size="sm" onClick={fetchItems}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>

          {loading && items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No privacy policy sections found. Add one above.</div>
          ) : (
            <div className="space-y-4">
              {items.map((item, i) => (
                <PrivacyPolicyItem
                  key={effectiveId(item) || i}
                  index={i}
                  item={item}
                  isEditing={editingId === effectiveId(item)}
                  onEdit={() => setEditingId(effectiveId(item) || null)}
                  onCancelEdit={() => setEditingId(null)}
                  onSave={handleUpdate}
                  onDelete={openDeleteDialog}
                  effectiveId={effectiveId}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!isDeleting) setDeleteDialog(prev => ({ ...prev, open }));
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Section"
        description={`Are you sure you want to delete "${deleteDialog.policyTitle}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PrivacyPolicyManagement;
